import React from "react";
import { PetakSawah } from "./games/PetakSawah";

// Latar tempat kerja (placeholder kerangka, akan diperkaya saat brainstorming tiap profesi).
// Langit + awan dibagi bersama; bagian tanah/gedung berbeda tiap profesi. SVG mengisi lebar layar, menempel ke bawah.
const LANGIT = {
  petani: ["#bfe0f3", "#eaf5fb"], tukang: ["#c9dff0", "#f1f0e8"], barista: ["#f4ead9", "#f8f3e8"], sopir: ["#a9cfd9", "#dcebe0"], penebang: ["#b9d9c8", "#eef6ee"],
};

function Awan({ x, y, s = 1 }) {
  return <g transform={`translate(${x} ${y}) scale(${s})`} fill="#fff" opacity=".92"><ellipse cx="0" cy="0" rx="34" ry="14" /><ellipse cx="-18" cy="4" rx="20" ry="11" /><ellipse cx="20" cy="5" rx="22" ry="12" /></g>;
}

// Dataran isometrik (bidang datar seperti peta kota) dengan garis grid 2:1 samar; `cakrawala` = elemen jauh di atas garis horizon y=62
function Dataran({ warna = "#8fcb70", cakrawala, children }) {
  return (
    <>
      {cakrawala}
      <rect x="0" y="62" width="400" height="338" fill={warna} />
      <clipPath id="jw-dataran"><rect x="0" y="62" width="400" height="338" /></clipPath>
      <g clipPath="url(#jw-dataran)" stroke="rgba(0,0,0,.06)" strokeWidth="1">
        {Array.from({ length: 24 }, (_, i) => -200 + i * 27.5).map(y0 => <path key={`a${y0}`} d={`M0 ${y0} L400 ${y0 + 200}`} />)}
        {Array.from({ length: 24 }, (_, i) => -200 + i * 27.5).map(y0 => <path key={`b${y0}`} d={`M400 ${y0} L0 ${y0 + 200}`} />)}
      </g>
      {children}
    </>
  );
}
const Pohon = ({ x, y, s = 1 }) => <g transform={`translate(${x} ${y}) scale(${s})`}><ellipse cy="2" rx="14" ry="5" fill="rgba(0,0,0,.15)" /><rect x="-2.5" y="-14" width="5" height="16" fill="#7a4e26" /><circle cy="-22" r="13" fill="#4f9a4a" /><circle cx="-8" cy="-14" r="9" fill="#5fae4e" /><circle cx="8" cy="-14" r="9" fill="#5fae4e" /></g>;
const ISO_DATARAN = new Set(["petani"]);

// Jalur hutan Sopir (idle): jalan layang berkelok isometrik di tengah bingkai (area terlihat di HP ≈ x 64–336, y < 330
// karena slice + bar Mulai), bus PO di tengah jalur, hutan pinus rapat, kabut horizon.
const isoP = (x, y) => [175 + (x - y) * 22, 290 - (x + y) * 11];
const poli = pts => pts.map(([x, y]) => isoP(x, y).join(",")).join(" ");
const RUTE = [[-4, -4], [-1, -4], [-1, -2], [2, -2], [2, 1], [5, 1], [5, 3], [8, 3], [8, 6]];
const PINUS_IDLE = [[80, 150, .7], [110, 135, .55], [300, 140, .6], [325, 160, .75], [90, 215, .9], [320, 225, .95], [75, 290, 1.2], [330, 300, 1.25], [105, 330, 1.3], [300, 345, 1.35], [135, 175, .65], [280, 120, .5], [200, 115, .5], [240, 125, .55], [150, 120, .5]];
function JalurHutan() {
  const seg = RUTE.slice(1).map((q, i) => [RUTE[i], q]);
  const sisi = [], atas = [];
  for (const [[x0, y0], [x1, y1]] of seg) {
    const lo = { x: Math.min(x0, x1) - 0.5, y: Math.min(y0, y1) - 0.5 }, hi = { x: Math.max(x0, x1) + 0.5, y: Math.max(y0, y1) + 0.5 };
    atas.push(poli([[lo.x, lo.y], [hi.x, lo.y], [hi.x, hi.y], [lo.x, hi.y]]));
    const [ax, ay] = isoP(lo.x, lo.y), [bx, by] = isoP(hi.x, lo.y), [cx, cy] = isoP(lo.x, hi.y);
    sisi.push(`${ax},${ay} ${bx},${by} ${bx},${by + 14} ${ax},${ay + 14}`, `${ax},${ay} ${cx},${cy} ${cx},${cy + 14} ${ax},${ay + 14}`);
  }
  const Pinus = ({ x, y, s, w = "#2f6a30" }) => <g transform={`translate(${x} ${y}) scale(${s})`}><rect x="-2.5" y="-8" width="5" height="16" fill="#4a2e16" /><path d="M-16 -4 L0 -40 L16 -4 Z" fill={w} /><path d="M-12 -18 L0 -50 L12 -18 Z" fill={w} /><path d="M-8 -32 L0 -60 L8 -32 Z" fill={w} /></g>;
  const [bx, by] = isoP(3.5, -2);
  return (
    <>
      <defs><linearGradient id="jw-kabut" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#c9dcd6" /><stop offset="1" stopColor="#2b5a33" /></linearGradient></defs>
      <rect x="0" y="0" width="400" height="400" fill="#2b5a33" />
      <rect x="0" y="0" width="400" height="130" fill="url(#jw-kabut)" />
      <path d="M0 112 L30 92 L60 108 L90 86 L120 104 L150 90 L180 106 L210 88 L240 104 L270 92 L300 108 L330 90 L360 106 L400 94 V140 H0 Z" fill="#43804a" opacity=".85" />
      <path d="M0 130 Q100 112 200 128 T400 124 V400 H0 Z" fill="#35703d" />
      {PINUS_IDLE.filter(([, y]) => y < 200).map(([x, y, s], i) => <Pinus key={`j${i}`} x={x} y={y} s={s} w={i % 2 ? "#2f6a30" : "#3c7d3e"} />)}
      {sisi.map((d, i) => <polygon key={`s${i}`} points={d} fill={i % 2 ? "#3a3f46" : "#2c3036"} />)}
      {atas.map((d, i) => <polygon key={`a${i}`} points={d} fill="#6b727a" stroke="#8a9199" strokeWidth="1" />)}
      <g transform={`translate(${bx} ${by}) scale(0.62)`}>
        <ellipse cx="0" cy="6" rx="42" ry="12" fill="rgba(0,0,0,.28)" />
        <polygon points="-40,-6 -10,-21 -10,-49 -40,-34" fill="#d9dde3" />
        <polygon points="-10,-21 44,-48 44,-76 -10,-49" fill="#f2f4f7" />
        <polygon points="-10,-49 44,-76 14,-91 -40,-64" fill="#ffffff" />
        <polygon points="-6,-28 40,-51 40,-61 -6,-38" fill="#2e58c4" />
        <polygon points="-6,-26 40,-49 40,-46 -6,-23" fill="#c62828" />
        <polygon points="-4,-48 38,-69 38,-60 -4,-39" fill="#1f3a66" opacity=".85" />
        <polygon points="-36,-26 -14,-37 -14,-30 -36,-19" fill="#1f3a66" />
        <circle cx="-30" cy="-6" r="4" fill="#c62828" /><circle cx="-16" cy="-13" r="4" fill="#c62828" />
        <ellipse cx="0" cy="-15" rx="6" ry="4" fill="#141414" /><ellipse cx="30" cy="-30" rx="6" ry="4" fill="#141414" />
      </g>
      {/* bekas ban drift di belakang bus */}
      <path d={`M${bx - 70} ${by + 18} q 18 -4 34 -12 t 26 -12`} stroke="#15171a" strokeWidth="3" strokeDasharray="7 5" fill="none" opacity=".75" />
      <path d={`M${bx - 66} ${by + 26} q 18 -4 34 -12 t 26 -12`} stroke="#15171a" strokeWidth="3" strokeDasharray="7 5" fill="none" opacity=".75" />
      {PINUS_IDLE.filter(([, y]) => y >= 200).map(([x, y, s], i) => <Pinus key={`d${i}`} x={x} y={y} s={s} w={i % 2 ? "#2f6a30" : "#3c7d3e"} />)}
    </>
  );
}

const TANAH = {
  petani: (
    <Dataran cakrawala={<>
      <path d="M0 52 Q50 28 110 50 T230 46 T330 42 T400 52 V62 H0 Z" fill="#b7cfc0" />
      <path d="M0 58 Q40 50 80 58 T160 57 T240 58 T320 56 T400 58 V66 H0 Z" fill="#6fae60" />
    </>}>
      {/* jalan tanah */}
      <path d="M0 356 L0 380 L400 180 L400 158 Z" fill="#c9a876" opacity=".9" />
      <path d="M0 368 L400 168" stroke="#b58f5c" strokeWidth="1.5" strokeDasharray="6 10" fill="none" />
      {[[88, 84, .8], [300, 88, .9], [352, 108, 1], [40, 120, 1]].map(([x, y, s], i) => <Pohon key={i} x={x} y={y} s={s} />)}
      <g transform="translate(104 124)"><ellipse cy="12" rx="34" ry="9" fill="rgba(0,0,0,.14)" /><path d="M-30 -6 L0 -30 L30 -6 Z" fill="#b8742d" /><path d="M-24 -6 H24 V8 H-24 Z" fill="#d9b57a" /><rect x="-20" y="-6" width="4" height="16" fill="#8a5a2b" /><rect x="16" y="-6" width="4" height="16" fill="#8a5a2b" /></g>
    </Dataran>
  ),
  tukang: (
    <>
      <path d="M0 205 H400 V400 H0 Z" fill="#d9d4c3" />
      <g fill="#aeb7c2" opacity=".9"><rect x="20" y="120" width="40" height="90" /><rect x="70" y="95" width="30" height="115" /><rect x="300" y="110" width="45" height="100" /><rect x="350" y="140" width="40" height="70" /></g>
      <g transform="translate(150 60)"><rect x="0" y="40" width="120" height="150" fill="#e9e4d4" stroke="#8a8776" strokeWidth="3" /><g stroke="#c27a1c" strokeWidth="3"><path d="M0 80 H120 M0 120 H120 M0 160 H120 M30 40 V190 M60 40 V190 M90 40 V190" /></g><rect x="-10" y="150" width="140" height="6" fill="#c27a1c" /><rect x="-10" y="110" width="140" height="6" fill="#c27a1c" /><rect x="-10" y="70" width="140" height="6" fill="#c27a1c" /></g>
      <g stroke="#55606e" strokeWidth="5" fill="none"><path d="M290 205 V20 H180" /><path d="M180 20 V40" /><circle cx="180" cy="48" r="6" fill="#c27a1c" stroke="none" /></g>
    </>
  ),
  barista: (
    <>
      <rect x="0" y="0" width="400" height="400" fill="#f1e6d2" />
      <rect x="40" y="60" width="120" height="110" rx="10" fill="#cfe6f4" stroke="#fff" strokeWidth="6" />
      <g stroke="#fff" strokeWidth="5"><path d="M100 60 V170 M40 115 H160" /></g>
      <g transform="translate(250 90)"><rect x="0" y="0" width="110" height="60" rx="6" fill="#fff" /><g stroke="#e5ddc9" strokeWidth="2"><path d="M0 20 H110 M0 40 H110" /></g>{[12, 40, 68, 96].map(x => <ellipse key={x} cx={x} cy="12" rx="9" ry="5" fill="#8a5a3c" />)}</g>
      <rect x="0" y="230" width="400" height="170" fill="#8a5a3c" /><rect x="0" y="222" width="400" height="14" fill="#a9734e" />
      <g transform="translate(60 180)"><rect x="0" y="0" width="60" height="42" rx="8" fill="#141414" /><rect x="8" y="-10" width="44" height="14" rx="4" fill="#3b3b3b" /><circle cx="48" cy="20" r="5" fill="#0000CD" /></g>
      {[200, 250, 300].map((x, i) => <g key={x} transform={`translate(${x} 195)`}><path d="M0 0 H28 L24 26 H4 Z" fill="#fff" /><path d="M28 6 q12 2 0 14" stroke="#fff" strokeWidth="4" fill="none" /><path d={`M8 -6 q3 -6 0 -12 M16 -4 q3 -6 0 -12`} stroke="#d9d4c3" strokeWidth="2" fill="none" opacity={.6 + i * .1} /></g>)}
    </>
  ),
  sopir: <JalurHutan />,
  penebang: (
    <>
      <path d="M0 150 Q60 110 120 140 T240 130 T400 145 V400 H0 Z" fill="#5f9a56" />
      <path d="M0 210 H400 V400 H0 Z" fill="#7fb86a" />
      <path d="M0 330 Q200 300 400 340 V400 H0 Z" fill="#a98457" opacity=".85" />
      {[[30, 215, 1.4], [80, 200, 1.1], [330, 205, 1.3], [380, 225, 1.1], [130, 190, .9], [290, 190, .9]].map(([x, y, s], i) => <g key={i} transform={`translate(${x} ${y}) scale(${s})`}><ellipse cy="2" rx="14" ry="5" fill="rgba(0,0,0,.15)" /><rect x="-2.5" y="-16" width="5" height="18" fill="#6e4523" /><path d="M-18 -10 L0 -44 L18 -10 Z" fill="#3f7d3a" /><path d="M-14 -24 L0 -52 L14 -24 Z" fill="#4a8f44" /></g>)}
      {[[200, 270], [230, 278], [215, 262]].map(([x, y], i) => <g key={i} transform={`translate(${x} ${y})`}><rect x="-40" y="-9" width="80" height="18" rx="9" fill="#9a6a3c" /><circle cx="40" cy="0" r="9" fill="#d9b57a" /><circle cx="40" cy="0" r="5" fill="#c49a62" /><circle cx="40" cy="0" r="2" fill="#a97d4c" /></g>)}
      <g transform="translate(90 300)"><rect x="-26" y="-10" width="52" height="20" rx="4" fill="#8a5a2b" /><rect x="-3" y="-46" width="6" height="38" fill="#6e4523" /><path d="M3 -46 h16 l-4 14 h-12 Z" fill="#b8bcc4" /></g>
    </>
  ),
};

export function Scene({ profesi = "petani", idle = true }) {
  const [a, b] = LANGIT[profesi] || LANGIT.petani;
  return (
    <div data-testid={`scene-${profesi}`} style={{ position: "absolute", inset: 0, background: `linear-gradient(${a}, ${b} 55%)`, overflow: "hidden" }}>
      <style>{`@keyframes jw-awan{0%{transform:translateX(0)}100%{transform:translateX(40px)}}`}</style>
      {profesi !== "barista" && (
        <svg viewBox="0 0 400 200" preserveAspectRatio="xMidYMin slice" style={{ position: "absolute", left: 0, right: 0, top: 0, width: "100%", height: "48%" }}>
          <circle cx="330" cy="70" r="26" fill="#ffd66b" opacity=".95" />
          <g style={{ animation: "jw-awan 18s ease-in-out infinite alternate" }}><Awan x={80} y={70} /><Awan x={260} y={110} s={.8} /></g>
        </svg>
      )}
      <svg viewBox="0 0 400 400" preserveAspectRatio="xMidYMax slice" style={{ position: "absolute", left: 0, right: 0, bottom: 0, width: "100%", height: ISO_DATARAN.has(profesi) ? "78%" : "72%" }}>
        {TANAH[profesi] || TANAH.petani}
      </svg>
      {idle && profesi === "petani" && <PetakSawah />}
    </div>
  );
}
